import FormModel from "./model/form-model";
import PageConfig from "./config";
import DashboardWidgetService from "../../../../../services/dashboard-widgets";
import { isEmpty, isFunction, orderBy } from "lodash";
import PostServiceApi from "../../../../../services/post-service";
import AppConfig from "../../../../../config";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import Config from "../../../../../config";
import { OTHER_COUNTRY_CODE } from "../../../../../config/constant";

const { FIELD_TYPE } = PageConfig;
const { REACT_APP_WEB_URL } = AppConfig;
const { ADS_OVERVIEW_PAGE_SIZE } = Config;

const formatDate = (date) => {
  const format = "YYYY-MMM-DD";
  return moment.utc(date).local().format(format);
};

const formatDateAndTime = (date) => {
  const format = "lll";
  return moment.utc(date).local().format(format);
};

const buildModelFromFormModel = () => {
  const output = {};
  for (const fieldMeta of Object.values(FormModel)) {
    switch (fieldMeta.fieldType) {
      case FIELD_TYPE.dateRangeSelectorFilter:
        output[fieldMeta.fieldName] = { fromDate: "", toDate: "" };
        break;
      case FIELD_TYPE.multiSelectFilterInput:
        output[fieldMeta.fieldName] = [];
        break;
      default:
        output[fieldMeta.fieldName] = fieldMeta.defaultValue ?? "";
        break;
    }
  }
  output.pageNumber = 1;
  return output;
};

const buildSearchParams = ({ values, pageNumber, pageSize }) => {
  return {
    pageSize: pageSize ?? ADS_OVERVIEW_PAGE_SIZE,
    pageNum: pageNumber,
    [FormModel.timeZone.fieldName]: values[FormModel.timeZone.fieldName],
    ...(!isEmpty(values[FormModel.adCreatedRange.fieldName]?.fromDate) && {
      createdAtFrom: values[FormModel.adCreatedRange.fieldName].fromDate,
    }),
    ...(!isEmpty(values[FormModel.adCreatedRange.fieldName]?.toDate) && {
      createdAtTo: values[FormModel.adCreatedRange.fieldName].toDate,
    }),

    ...(!isEmpty(values[FormModel.adExpriedRange.fieldName]?.fromDate) && {
      expiresAtFrom: values[FormModel.adExpriedRange.fieldName].fromDate,
    }),
    ...(!isEmpty(values[FormModel.adExpriedRange.fieldName]?.toDate) && {
      expiresAtTo: values[FormModel.adExpriedRange.fieldName].toDate,
    }),

    ...(!isEmpty(
      values[FormModel.adFirstPublishedRange?.fieldName]?.fromDate,
    ) && {
      firstPublishedAtFrom:
        values[FormModel.adFirstPublishedRange?.fieldName].fromDate,
    }),
    ...(!isEmpty(
      values[FormModel.adFirstPublishedRange?.fieldName]?.toDate,
    ) && {
      firstPublishedAtTo:
        values[FormModel.adFirstPublishedRange?.fieldName].toDate,
    }),

    ...(!isEmpty(
      values[FormModel.adLastPublishedRange?.fieldName]?.fromDate,
    ) && {
      publishedAtFrom:
        values[FormModel.adLastPublishedRange?.fieldName].fromDate,
    }),
    ...(!isEmpty(values[FormModel.adLastPublishedRange?.fieldName]?.toDate) && {
      publishedAtTo: values[FormModel.adLastPublishedRange.fieldName].toDate,
    }),
    ...(!isEmpty(values[FormModel.profileStatus.fieldName]) && {
      status: values[FormModel.profileStatus.fieldName].toString(),
    }),
    ...(!isEmpty(values[FormModel.pricingTier.fieldName]) && {
      pricingTier: values[FormModel.pricingTier.fieldName].toString(),
    }),
    ...(!isEmpty(values[FormModel.subscriptionStatus.fieldName]) && {
      subStatus: values[FormModel.subscriptionStatus.fieldName].toString(),
    }),
    ...(!isEmpty(values[FormModel.residentCountry.fieldName]) && {
      residentCountries: values[FormModel.residentCountry.fieldName].toString(),
    }),
    ...(!isEmpty(values[FormModel.sortOrder.fieldName]) && {
      sortBy: values[FormModel.sortOrder.fieldName],
    }),
  };
};

const setSearchResults = async ({ values, setFieldValue, setResults }) => {
  const searchParams = buildSearchParams({
    values,
    pageNumber: values.pageNumber - 1,
  });
  const response = await DashboardWidgetService.searchPosts({ searchParams });
  if (!response.success) {
    throw new Error("Unable to get search results");
  }

  const result = response?.body ?? {};

  setResults(result.results ?? []);
  setFieldValue("totalResults", result.total);
  setFieldValue("pageNumber", result.pageNum + 1);
  setFieldValue("pageSize", result.pageSize);
  setFieldValue("pageCount", result.pageCount);
};

const sanitizeSortOrder = ({ sortBy = {}, sortDirection = {} }) => {
  const combinedObj = Object.keys(sortBy).reduce((acc, key) => {
    acc[key] = {
      checked: sortBy[key],
      order: sortDirection[key] ?? null,
    };
    return acc;
  }, {});

  let tempArr = [];
  for (const [key, value] of Object.entries(combinedObj)) {
    if (value?.checked) {
      const str = `${key}:${value?.order ?? "asc"}`;
      tempArr.push(str);
    }
  }
  return tempArr.toString();
};

const formatUserData = ({ userData }) => {
  const advertisement = userData ?? {};
  return {
    fullName: `${advertisement?.post?.firstName ?? ""} ${
      advertisement.post?.lastName
    }`,
    memberId: advertisement?.memberId ?? "",
    postId: advertisement?.post?.id ?? "",
    phoneNumber: advertisement?.phone ?? "",
    email: advertisement?.email ?? "N/A",
    status: advertisement?.post?.status ?? "",
    pricingTier: advertisement?.post?.pricingTier?.id ?? "N/A",
    subscriptionStatus: advertisement?.post?.subscriptionStatus ?? "N/A",
    age: advertisement?.post?.age ?? "",
    civilStatus: advertisement?.post?.civilStatus?.status ?? "N/A",
    residentCountry:
      advertisement?.post?.residentCountry?.code === OTHER_COUNTRY_CODE
        ? (advertisement?.post?.residentCountryOther?.name ?? "Other Country")
        : (advertisement?.post?.residentCountry?.name ?? "N/A"),
    createdAt: !isEmpty(advertisement?.post?.createdAt)
      ? formatDate(advertisement?.post?.createdAt)
      : "N/A",
    firstPublishedAt: advertisement?.post?.firstPublishedAt
      ? formatDate(advertisement?.post?.firstPublishedAt)
      : "N/A",
    publishedAt: !isEmpty(advertisement?.post?.publishedAt)
      ? formatDate(advertisement?.post?.publishedAt)
      : "N/A",
    expiresAt: !isEmpty(advertisement?.post?.expiresAt)
      ? formatDate(advertisement?.post?.expiresAt)
      : "N/A",
    href: `${REACT_APP_WEB_URL}/post?id=${advertisement?.post?.id ?? ""}`,
  };
};

const orderResultsByTimeDescending = (results = []) => {
  return orderBy(results, ["createdAt"], ["desc"]);
};

const getUserPaymentHistory = async ({
  postId,
  setTableData,
  setIsFetchingData,
}) => {
  try {
    setIsFetchingData(true);
    const response = await PostServiceApi.getUserPaymentHistory({ postId });
    if (!response.success) {
      NotificationManager.error("Unable to get payment history!", "Error");
      return;
    }
    const results = response.body ?? {};
    const sortedResultSet = {
      postPayments: orderResultsByTimeDescending(results.postPayments ?? []),
      featuredPaymentHistory: orderResultsByTimeDescending(
        results.featuredPaymentHistory ?? [],
      ),
    };

    setTableData(sortedResultSet);
  } catch (error) {
    console.log(error);
  } finally {
    setIsFetchingData(false);
  }
};

const getUserSubscriptionAttempts = async ({
  memberId,
  setTableData,
  setIsFetching,
}) => {
  try {
    setIsFetching(true);
    const response = await PostServiceApi.getUserSubscriptionAttempts({
      memberId,
    });
    if (!response.success) {
      NotificationManager.error(
        "Unable to get subscription attempts!",
        "Error",
      );
      return;
    }
    setTableData(orderResultsByTimeDescending(response.body ?? []));
  } catch (error) {
    console.log("Error >>>", error);
  } finally {
    setIsFetching(false);
  }
};

const exportResultsToEmail = async ({
  values,
  closeModal,
  filterValues,
  actions,
}) => {
  let searchParams = buildSearchParams({
    values: filterValues,
    pageSize: 0,
    pageNumber: 0,
  });
  searchParams.email = values.email;
  try {
    const response = await DashboardWidgetService.searchPosts({ searchParams });
    if (!response.success) {
      throw new Error("Failed to send export");
    }
    NotificationManager.success("Export sent successfully! Check your email");
    closeModal();
  } catch (error) {
    isFunction(actions.setSubmitting) && actions.setSubmitting(false);
    NotificationManager.error("Something went wrong. Try again later");
    console.log(error);
  }
};

const isValidDateRange = (fromDate, toDate) => {
  const start = moment(fromDate);
  const end = moment(toDate);
  return start.isValid() && end.isValid();
};

const timeZoneSelectorVisibility = (values = {}) => {
  return (
    isValidDateRange(
      values.adCreatedRange?.fromDate,
      values.adCreatedRange?.toDate,
    ) ||
    isValidDateRange(
      values.adExpriedRange?.fromDate,
      values.adExpriedRange?.toDate,
    ) ||
    isValidDateRange(
      values.adFirstPublishedRange?.fromDate,
      values.adFirstPublishedRange?.toDate,
    ) ||
    isValidDateRange(
      values.adLastPublishedRange?.fromDate,
      values.adLastPublishedRange?.toDate,
    )
  );
};

const PageDomain = Object.freeze({
  buildModelFromFormModel,
  setSearchResults,
  buildSearchParams,
  getUserPaymentHistory,
  getUserSubscriptionAttempts,
  sanitizeSortOrder,
  formatUserData,
  formatDate,
  formatDateAndTime,
  exportResultsToEmail,
  timeZoneSelectorVisibility,
});
export default PageDomain;
